import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import {
  CorporativeClientService,
  WalletPixMovement,
  saveBlobAsPdf,
} from '@rsm/client/corporative-client';
import { MessageService } from '@rsm/shared/message';
import { from } from 'rxjs';
import { PixPaymentMethod } from './pix-payment-display.component';

/**
 * Painel padronizado de pagamento via PIX (+ cartão "Em breve") — gera um PIX
 * real (BS2) para o valor informado, faz o polling silencioso até confirmar,
 * e emite `confirmed` com o novo saldo da carteira.
 *
 * Usado tanto no checkout de crédito da home quanto no checkout de campanha —
 * único lugar que mantém esse fluxo, pra não duplicar lógica nem visual.
 */
@Component({
  selector: 'rsm-pix-payment-panel',
  templateUrl: './pix-payment-panel.component.html',
  styleUrls: ['./pix-payment-panel.component.scss'],
})
export class PixPaymentPanelComponent implements OnDestroy {
  /** Contexto da empresa que está pagando. */
  @Input() contextId = '';

  /** Valor a cobrar via PIX. */
  @Input() amount = 0;

  /** Valor referente as licenças */
  @Input() amountLicenses = 0;

  /** Valor referente os creditos */
  @Input() amountCredits = 0;

  /** Quantidade de licenças a conceder quando o pagamento for confirmado. */
  @Input() licensesQuantity = 0;

  // Textos do boleto renderizado na tela (<rsm-boleto-view>).
  @Input() boletoDescription = 'Crédito de saldo em carteira';
  @Input() boletoDescriptionDetail = 'Recarga de créditos para uso na plataforma';
  @Input() boletoInfoLine = '';

  /** Emitido quando o pagamento é confirmado — carrega o novo saldo da carteira. */
  @Output() confirmed = new EventEmitter<{ newBalance: number }>();

  /**
   * Emitido sempre que o estado "tem um PIX gerado/em aberto" muda — útil pro
   * pai esconder campos que não devem mudar depois que o valor foi travado
   * num QR code (ex.: "incluir crédito adicional").
   */
  @Output() generatedChange = new EventEmitter<boolean>();

  public paymentMethod: PixPaymentMethod = 'pix';

  /** QR code real retornado pelo BS2. */
  public paymentReference = '';

  /** Id do movimento de recarga gerado — usado pra baixar o boleto (mesmo QR). */
  public movementId = '';

  /** Vencimento da cobrança (dateExpire do PIX) — exibido no boleto. */
  public dueDate: string | null = null;

  /** True enquanto o POST de geração do PIX está em voo. */
  public isGeneratingPix = false;

  /** True enquanto o boleto (PDF) está sendo gerado/baixado. */
  public isGeneratingBoleto = false;

  /** True depois que um PIX foi gerado (aguardando ou já pago). */
  public pixGenerated = false;

  /** True quando o polling confirma o pagamento (saldo já foi creditado). */
  public pixConfirmed = false;

  /** True enquanto o polling está ativo, consultando o backend silenciosamente. */
  public pollingActive = false;

  /** True após 10 min de polling sem confirmação — o scheduler vira o fallback. */
  public pollingTimedOut = false;

  private pollingTimer: ReturnType<typeof setTimeout> | null = null;
  private pollingElapsedSecs = 0;

  constructor(
    private corporativeService: CorporativeClientService,
    private messageService: MessageService
  ) {}

  ngOnDestroy(): void {
    this.stopPolling();
  }

  public selectPaymentMethod(method: PixPaymentMethod): void {
    if (this.paymentMethod === method) return;
    this.paymentMethod = method;
  }

  public generatePix(): void {
    if (!this.contextId || this.amount <= 0 || this.isGeneratingPix) return;

    this.isGeneratingPix = true;

    this.createMovement(
      () => (this.isGeneratingPix = false),
      () => {
        this.isGeneratingPix = false;
        this.messageService.addMsgDanger({
          message: 'Erro ao gerar o PIX. Verifique sua conexão e tente novamente.',
          autoclose: true,
        });
      }
    );
  }

  /**
   * Gera o boleto: cria o movimento (se ainda não houver) e mostra o boleto na
   * tela (<rsm-boleto-view>). Se um PIX já foi gerado, só revela a tela.
   */
  public generateBoleto(): void {
    if (this.amount <= 0 || this.isGeneratingBoleto) return;

    if (this.pixGenerated && this.movementId) return;

    if (!this.contextId) return;

    this.isGeneratingBoleto = true;
    this.createMovement(
      () => (this.isGeneratingBoleto = false),
      () => {
        this.isGeneratingBoleto = false;
        this.messageService.addMsgDanger({
          message: 'Erro ao gerar o boleto. Verifique sua conexão e tente novamente.',
          autoclose: true,
        });
      }
    );
  }

  /**
   * (Gera o movimento, se necessário, e) baixa o boleto bolepix em PDF.
   * Reusa o MESMO movimento/QR do PIX — se um PIX já foi gerado, só baixa o PDF.
   */
  public downloadBoleto(): void {
    if (this.amount <= 0 || this.isGeneratingBoleto) return;

    this.isGeneratingBoleto = true;

    if (this.pixGenerated && this.movementId) {
      this.fetchBoleto();
      return;
    }

    if (!this.contextId) {
      this.isGeneratingBoleto = false;
      return;
    }

    this.createMovement(
      () => this.fetchBoleto(),
      () => {
        this.isGeneratingBoleto = false;
        this.messageService.addMsgDanger({
          message: 'Erro ao gerar o boleto. Verifique sua conexão e tente novamente.',
          autoclose: true,
        });
      }
    );
  }

  /** Cria o movimento de recarga (PIX real BS2) e inicia o polling. */
  private createMovement(done: () => void, fail: () => void): void {

    const body = {
      contextId: this.contextId,
      billingValue: this.amount,
      billingLicensesValue: this.amountLicenses,
      billingCreditsValue: this.amountCredits,
      licensesQuantity: this.licensesQuantity,
    };

    this.corporativeService
      .createWalletCreditPix(body)
      .subscribe({
        next: (movement: WalletPixMovement) => {
          this.paymentReference = movement.qrCode;
          this.movementId = movement.movementId;
          this.dueDate = movement.dateExpire;
          this.pixGenerated = true;
          this.pixConfirmed = false;
          this.generatedChange.emit(true);
          this.startPolling();
          done();
        },
        error: () => fail(),
      });
  }

  /** Baixa o PDF do boleto do movimento atual. */
  private fetchBoleto(): void {
    this.corporativeService.downloadWalletBoleto(this.movementId).subscribe({
      next: (blob) => {
        saveBlobAsPdf(blob, 'boleto-rsm-credito.pdf');
        this.isGeneratingBoleto = false;
      },
      error: () => {
        this.isGeneratingBoleto = false;
        this.messageService.addMsgDanger({
          message: 'Erro ao gerar o boleto. Tente novamente.',
          autoclose: true,
        });
      },
    });
  }

  /** Descarta o PIX/boleto atual e volta ao estado inicial. */
  public retryPix(): void {
    this.stopPolling();
    this.paymentReference = '';
    this.movementId = '';
    this.dueDate = null;
    this.pixGenerated = false;
    this.pixConfirmed = false;
    this.pollingTimedOut = false;
    this.generatedChange.emit(false);
  }

  public copyCode(): void {
    if (!this.paymentReference) return;

    from(navigator.clipboard.writeText(this.paymentReference)).subscribe({
      next: () => {
        this.messageService.addMsgSuccess({
          message: 'Código PIX copiado com sucesso!',
          autoclose: true,
        });
      },
      error: () => {
        this.messageService.addMsgDanger({
          message: 'Não foi possível copiar o código.',
          autoclose: true,
        });
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Polling em camadas: 0–120s a cada 5s, 120–600s a cada 15s, depois para.
  // ---------------------------------------------------------------------------

  private startPolling(): void {
    this.pollingElapsedSecs = 0;
    this.pollingActive = true;
    this.pollingTimedOut = false;

    // O overlay não é mais suprimido globalmente durante os até 10 min de
    // polling — isso deixava o app inteiro sem carregamento nesse período. A
    // própria chamada de status já roda silenciosa (`skipLoader`).
    this.scheduleNextPoll();
  }

  private scheduleNextPoll(): void {
    const intervalMs = this.pollingElapsedSecs < 120 ? 5_000 : 15_000;
    this.pollingTimer = setTimeout(() => this.pollOnce(), intervalMs);
  }

  private pollOnce(): void {
    this.corporativeService.checkWalletCreditPayment(this.contextId).subscribe({
      next: (status) => {
        if (status.status === 'CONFIRMED') {
          this.stopPolling();
          this.pixConfirmed = true;
          this.confirmed.emit({ newBalance: status.newBalance ?? 0 });
          return;
        }

        this.advanceAndReschedule();
      },
      error: () => {
        // Erro transitório de rede — segue tentando em vez de incomodar o usuário.
        this.advanceAndReschedule();
      },
    });
  }

  private advanceAndReschedule(): void {
    const stepSecs = this.pollingElapsedSecs < 120 ? 5 : 15;
    this.pollingElapsedSecs += stepSecs;

    if (this.pollingElapsedSecs >= 600) {
      this.stopPolling();
      this.pollingTimedOut = true;
      return;
    }

    this.scheduleNextPoll();
  }

  private stopPolling(): void {
    if (this.pollingTimer != null) {
      clearTimeout(this.pollingTimer);
      this.pollingTimer = null;
    }
    this.pollingActive = false;
  }
}
